import {cn} from '@tempmee/core'
import {type CSSProperties, forwardRef} from 'react'
import {Box, type BoxProps} from './Box'
import {GridVariantsFactory, buildGapClasses} from './Variants'
import type {SpacingScale} from './Variants'

export interface GridProps extends BoxProps {
  /** Number of columns → `grid-cols-{n}`. Pass a string for a custom `gridTemplateColumns` value. */
  columns?: number | string
  /** Number of rows → `grid-rows-{n}`. Pass a string for a custom `gridTemplateRows` value. */
  rows?: number | string
  /** Align items along the block axis. Maps to `align-items`. */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline'
  /** Justify items along the inline axis. Maps to `justify-content`. */
  justify?: 'start' | 'center' | 'end' | 'between'
  /** Gap between all cells. Uses the spacing scale (0–24). */
  gap?: SpacingScale
  /** Horizontal gap between columns. Uses the spacing scale (0–24). */
  gapX?: SpacingScale
  /** Vertical gap between rows. Uses the spacing scale (0–24). */
  gapY?: SpacingScale
}

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  ({className, columns, rows, align, justify, gap, gapX, gapY, style, ...props}, ref) => {
    const gridClasses = GridVariantsFactory({align, justify})
    const gapClasses = buildGapClasses({gap, gapX, gapY})
    const colsClass = typeof columns === 'number' ? `grid-cols-${columns}` : ''
    const rowsClass = typeof rows === 'number' ? `grid-rows-${rows}` : ''

    const inlineStyle: CSSProperties = {}
    if (typeof columns === 'string') inlineStyle.gridTemplateColumns = columns
    if (typeof rows === 'string') inlineStyle.gridTemplateRows = rows

    return (
      <Box
        ref={ref}
        className={cn(gridClasses, gapClasses, colsClass, rowsClass, className)}
        style={{...inlineStyle, ...style}}
        {...props}
      />
    )
  },
)

Grid.displayName = 'Grid'
